import React, { useState, useEffect, useRef } from "react";
import { X, ChevronDown, Trash, Camera } from "lucide-react";
import {
  updatePet,
  getMedicalHistory,
  updateMedicalHistory,
  addMedicalHistory,
} from "../../../services/api";

const EditPetModal = ({ isOpen, onClose, onSuccess, onError, pet }) => {
  const [loading, setLoading] = useState(false);
  const [isBreedDropdownOpen, setIsBreedDropdownOpen] = useState(false);
  const [filteredBreeds, setFilteredBreeds] = useState([]);
  const breedDropdownRef = useRef(null);
  const [allergies, setAllergies] = useState("");
  const [vaccinations, setVaccinations] = useState([{ name: "", date: "" }]);
  const [dewormings, setDewormings] = useState([{ name: "", date: "" }]);
  const [neuteredEntries, setNeuteredEntries] = useState([
    { status: "", date: "" },
  ]);
  const [existingMedicalHistory, setExistingMedicalHistory] = useState(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);

  const handleAddVaccination = () => {
    setVaccinations([...vaccinations, { name: "", date: "" }]);
  };

  const handleAddDeworming = () => {
    setDewormings([...dewormings, { name: "", date: "" }]);
  };

  const handleNeuteredStatusChange = (index, value) => {
    const updated = [...neuteredEntries];
    updated[index].status = value;
    if (value === "no") {
      updated[index].date = "";
    }
    setNeuteredEntries(updated);
  };

  const handleAddNeutered = () => {
    setNeuteredEntries([...neuteredEntries, { status: "", date: "" }]);
  };

  const handleDeleteVaccination = (index) => {
    if (vaccinations.length > 1) {
      const updated = vaccinations.filter((_, i) => i !== index);
      setVaccinations(updated);
    }
  };

  const handleDeleteDeworming = (index) => {
    if (dewormings.length > 1) {
      const updated = dewormings.filter((_, i) => i !== index);
      setDewormings(updated);
    }
  };

  const handleDeleteNeutered = (index) => {
    if (neuteredEntries.length > 1) {
      const updated = neuteredEntries.filter((_, i) => i !== index);
      setNeuteredEntries(updated);
    }
  };

  // Form state with image field
  const [petForm, setPetForm] = useState({
    name: "",
    species: "",
    breed: "",
    dateOfBirth: "",
    weight: "",
    gender: "",
    color: "",
    image: "", // Added image field
  });

  // Breed options based on species
  const breedOptions = {
    Dog: [
      "Indian Desi (Non Descript)",
      "Labrador Retriever",
      "German Shepherd",
      "Golden Retriever",
      "Doberman Pinscher",
      "Pomeranian",
      "Pug",
      "Shih Tzu",
      "Other",
    ],
    Cat: [
      "Indian Bill (Desi Cat)",
      "Persian",
      "Moggie (Cross breed)",
      "Bengal Cat",
      "Bombay",
      "Maine Coon",
      "Siamese",
      "Himalayan Cat",
      "Other",
    ],
    Rabbit: [
      "Soviet Chinchilla",
      "New Zealand White",
      "White Giant",
      "Grey Giant",
      "Other",
    ],
    Bird: ["Cockatiels", "Budgies (Parakeet)", "Love Birds", "Other"],
    Fish: [
      "Goldfish",
      "Betta (Siamese Fighting Fish)",
      "Guppy",
      "Molly",
      "Platy",
      "Zebra Danio",
      "Tetra (Neon, Glowlight)",
      "Angelfish",
      "Other",
    ],
  };

  // Update form when pet data changes
  useEffect(() => {
    if (pet) {
      setPetForm({
        name: pet.name || "",
        species: pet.species || "",
        breed: pet.breed || "",
        dateOfBirth: pet.dateOfBirth
          ? new Date(pet.dateOfBirth).toISOString().split("T")[0]
          : "",
        weight: pet.weight ? pet.weight.toString() : "",
        gender: pet.gender || "",
        color: pet.color || "",
        image:
          pet.image ||
          (pet.photos && pet.photos.length > 0 ? pet.photos[0] : ""), // Handle both image field and photos array
      });

      // Load medical history
      loadMedicalHistory(pet._id);
    }
  }, [pet]);

  const loadMedicalHistory = async (petId) => {
    try {
      const medicalHistory = await getMedicalHistory(petId);
      if (medicalHistory.success && medicalHistory.data) {
        const data = medicalHistory.data;
        setExistingMedicalHistory(data);

        // Set allergies
        setAllergies(data.allergies || "");

        // Set vaccinations
        if (data.vaccinations && data.vaccinations.length > 0) {
          setVaccinations(
            data.vaccinations.map((v) => ({
              name: v.name || "",
              date: v.date ? v.date.split("T")[0] : "", // Format date for input
            }))
          );
        }

        // Set dewormings
        if (data.dewormings && data.dewormings.length > 0) {
          setDewormings(
            data.dewormings.map((d) => ({
              name: d.name || "",
              date: d.date ? d.date.split("T")[0] : "", // Format date for input
            }))
          );
        }

        // Set spayed/neutered
        if (data.spayedNeutered && data.spayedNeutered.length > 0) {
          setNeuteredEntries(
            data.spayedNeutered.map((s) => ({
              status: s.status || "",
              date: s.date ? s.date.split("T")[0] : "", // Format date for input
            }))
          );
        }
      }
    } catch (error) {
      console.error("Failed to load medical history:", error);
      // Don't show error to user, just use default empty values
    }
  };

  const resetForm = () => {
    setPetForm({
      name: "",
      species: "",
      breed: "",
      dateOfBirth: "",
      weight: "",
      gender: "",
      color: "",
      image: "",
    });

    // Reset medical details
    setAllergies("");
    setVaccinations([{ name: "", date: "" }]);
    setDewormings([{ name: "", date: "" }]);
    setNeuteredEntries([{ status: "", date: "" }]);
    setExistingMedicalHistory(null);

    setIsBreedDropdownOpen(false);
    setFilteredBreeds([]);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSpeciesChange = (e) => {
    const selectedSpecies = e.target.value;
    setPetForm({
      ...petForm,
      species: selectedSpecies,
      // Keep the current breed when species changes in edit mode
      // Only reset if the current breed is not compatible with new species
    });
    setIsBreedDropdownOpen(false);
    setFilteredBreeds([]);
  };

  const handleBreedChange = (e) => {
    const value = e.target.value;
    setPetForm({ ...petForm, breed: value });

    // Filter breeds based on input
    if (breedOptions[petForm.species] && value) {
      const filtered = breedOptions[petForm.species].filter((breed) =>
        breed.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredBreeds(filtered);
      setIsBreedDropdownOpen(filtered.length > 0);
    } else if (breedOptions[petForm.species]) {
      setFilteredBreeds(breedOptions[petForm.species]);
      setIsBreedDropdownOpen(true);
    }
  };

  const handleBreedSelect = (breed) => {
    setPetForm({ ...petForm, breed });
    setIsBreedDropdownOpen(false);
    setFilteredBreeds([]);
  };

  const handleBreedInputFocus = () => {
    if (breedOptions[petForm.species]) {
      setFilteredBreeds(breedOptions[petForm.species]);
      setIsBreedDropdownOpen(true);
    }
  };

  // Pet image upload function
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      onError("Please select a valid image file (JPEG, PNG, GIF, or WebP).");
      return;
    }

    // Validate file size (e.g., max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    if (file.size > maxSize) {
      onError("File size must be less than 5MB.");
      return;
    }

    setIsUploadingImage(true);

    const uploadFormData = new FormData();
    uploadFormData.append("file", file);
    uploadFormData.append("upload_preset", "mypreset");
    uploadFormData.append("cloud_name", "dsi1mb2aj");

    try {
      const response = await fetch(
        `https://api.cloudinary.com/v1_1/dsi1mb2aj/image/upload`,
        {
          method: "POST",
          body: uploadFormData,
        }
      );

      if (!response.ok) {
        throw new Error("Upload failed");
      }

      const data = await response.json();
      const imageUrl = data.secure_url;

      // Update the form data with the new image URL
      setPetForm((prevData) => ({
        ...prevData,
        image: imageUrl,
      }));

      onSuccess && onSuccess("Pet image uploaded successfully!");
    } catch (error) {
      console.error("Error uploading pet image:", error);
      onError("Failed to upload pet image. Please try again.");
    } finally {
      setIsUploadingImage(false);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        breedDropdownRef.current &&
        !breedDropdownRef.current.contains(event.target)
      ) {
        setIsBreedDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleSubmit = async () => {
    if (!pet) return;

    // Validate form
    if (
      !petForm.name ||
      !petForm.species ||
      !petForm.breed ||
      !petForm.dateOfBirth ||
      !petForm.weight ||
      !petForm.gender ||
      !petForm.color
    ) {
      onError("Please fill in all required fields");
      return;
    }

    try {
      setLoading(true);

      const petData = {
        ...petForm,
        dateOfBirth: petForm.dateOfBirth,
        weight: parseFloat(petForm.weight),
      };

      // Update pet first
      const response = await updatePet(pet._id, petData);

      if (response.success) {
        // Prepare medical data
        const medicalData = {
          allergies: allergies || "",
          vaccinations: vaccinations.filter(
            (vax) => vax.name.trim() || vax.date
          ),
          dewormings: dewormings.filter(
            (entry) => entry.name.trim() || entry.date
          ),
          spayedNeutered: neuteredEntries.filter((entry) => entry.status),
        };

        // Check if there's any medical data to save
        const hasMedicalData =
          medicalData.allergies ||
          medicalData.vaccinations.length > 0 ||
          medicalData.dewormings.length > 0 ||
          medicalData.spayedNeutered.length > 0;

        // Update or add medical history
        try {
          if (hasMedicalData) {
            if (existingMedicalHistory) {
              // Update existing medical history
              await updateMedicalHistory(pet._id, medicalData);
            } else {
              // Add new medical history
              await addMedicalHistory(pet._id, medicalData);
            }
          }

          onSuccess("Pet and medical details updated successfully!");
        } catch (medicalError) {
          console.error("Failed to update medical history:", medicalError);
          onSuccess(
            "Pet updated successfully, but there was an issue updating medical details."
          );
        }

        onClose();
      } else {
        onError(response.message || "Failed to update pet");
      }
    } catch (err) {
      onError(err.message || "Failed to update pet");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-4 sm:p-6 ">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-4">
              {/* Pet Image Display */}
              <div className="relative">
                <div className="w-16 h-16 bg-gray-100 rounded-full overflow-hidden">
                  <img
                    src={
                      petForm.image ||
                      `https://via.placeholder.com/150/e5e7eb/9ca3af?text=${
                        petForm.species?.charAt(0) || "P"
                      }`
                    }
                    alt="Pet"
                    className="w-full h-full object-cover"
                  />
                  {isUploadingImage && (
                    <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    </div>
                  )}
                </div>
                {/* Camera icon overlay */}
                <div className="absolute -bottom-1 -right-1 bg-blue-500 rounded-full p-1">
                  <Camera className="w-3 h-3 text-white" />
                </div>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Edit Pet</h2>
                <p className="text-sm text-gray-500">Update pet information</p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600"
              disabled={loading}
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Pet Name *
                </label>
                <input
                  type="text"
                  value={petForm.name}
                  onChange={(e) =>
                    setPetForm({ ...petForm, name: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                  disabled={loading}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Pet Photo
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  disabled={isUploadingImage || loading}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 file:mr-3 file:py-1 file:px-3 file:rounded file:border-0 file:text-sm file:bg-gray-50 file:text-gray-700 hover:file:bg-gray-100 disabled:opacity-50"
                />
                <p className="mt-1 text-xs text-gray-500">
                  JPEG, PNG, GIF, or WebP. Max 5MB.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Species *
                </label>
                <select
                  value={petForm.species}
                  onChange={handleSpeciesChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                  disabled={loading}
                  required
                >
                  <option value="">Select species</option>
                  <option value="Dog">Dog</option>
                  <option value="Cat">Cat</option>
                  <option value="Rabbit">Rabbit</option>
                  <option value="Bird">Pet Birds</option>
                  <option value="Fish">Fish</option>
                  
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Breed *
                </label>
                {breedOptions[petForm.species] ? (
                  <div className="relative" ref={breedDropdownRef}>
                    <div className="relative">
                      <input
                        type="text"
                        value={petForm.breed}
                        onChange={handleBreedChange}
                        onFocus={handleBreedInputFocus}
                        className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                        placeholder="Current breed or select/type new"
                        disabled={loading}
                        required
                      />
                      <button
                        type="button"
                        onClick={() => {
                          if (isBreedDropdownOpen) {
                            setIsBreedDropdownOpen(false);
                          } else {
                            handleBreedInputFocus();
                          }
                        }}
                        className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-400 hover:text-gray-600"
                        disabled={loading}
                      >
                        <ChevronDown
                          className={`w-4 h-4 transition-transform duration-200 ${
                            isBreedDropdownOpen ? "rotate-180" : ""
                          }`}
                        />
                      </button>
                    </div>

                    {isBreedDropdownOpen && filteredBreeds.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        {filteredBreeds.map((breed, index) => (
                          <button
                            key={breed}
                            type="button"
                            onClick={() => handleBreedSelect(breed)}
                            className={`w-full px-3 py-2 text-left hover:bg-blue-50 hover:text-blue-700 transition-colors duration-150 ${
                              index === 0 ? "rounded-t-lg" : ""
                            } ${
                              index === filteredBreeds.length - 1
                                ? "rounded-b-lg"
                                : ""
                            } ${
                              petForm.breed === breed
                                ? "bg-blue-100 text-blue-700"
                                : "text-gray-700"
                            }`}
                            disabled={loading}
                          >
                            {breed}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <input
                    type="text"
                    value={petForm.breed}
                    onChange={handleBreedChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                    placeholder="Enter breed"
                    disabled={loading}
                    required
                  />
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Date of Birth *
                </label>
                <input
                  type="date"
                  value={petForm.dateOfBirth}
                  onChange={(e) =>
                    setPetForm({ ...petForm, dateOfBirth: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                  disabled={loading}
                  required
                  max={new Date().toISOString().split("T")[0]} // Prevents selecting future dates
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Weight (kg) *
                </label>
                <input
                  type="number"
                  min="0"
                  max="1000"
                  step="0.1"
                  value={petForm.weight}
                  onChange={(e) =>
                    setPetForm({ ...petForm, weight: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                  disabled={loading}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Gender *
                </label>
                <select
                  value={petForm.gender}
                  onChange={(e) =>
                    setPetForm({ ...petForm, gender: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                  disabled={loading}
                  required
                >
                  <option value="">Select gender</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                </select>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Color *
                </label>
                <input
                  type="text"
                  value={petForm.color}
                  onChange={(e) =>
                    setPetForm({ ...petForm, color: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                  disabled={loading}
                  required
                />
              </div>
            </div>

            <div className="max-w-full overflow-hidden">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">
                Medical Details
              </h2>

              {/* Allergies */}
              <div className="mb-6">
                <label className="block font-medium text-gray-700 mb-1">
                  History of any previous disease or allergies?
                </label>
                <textarea
                  className="w-full p-2 border border-gray-300 rounded text-sm sm:text-base"
                  placeholder="Enter details here"
                  value={allergies}
                  onChange={(e) => setAllergies(e.target.value)}
                  disabled={loading}
                />
              </div>

              {/* Vaccination Section */}
              <div className="mb-6">
                <label className="block font-semibold text-gray-800 mb-2">
                  Vaccination Details
                </label>

                {vaccinations.map((vax, index) => (
                  <div
                    key={index}
                    className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4 mb-3 md:mb-2"
                  >
                    <input
                      type="text"
                      className="w-full md:flex-1 p-2 border border-gray-300 rounded text-sm sm:text-base"
                      placeholder="Which vaccination?"
                      value={vax.name}
                      onChange={(e) => {
                        const updated = [...vaccinations];
                        updated[index].name = e.target.value;
                        setVaccinations(updated);
                      }}
                      disabled={loading}
                    />
                    <input
                      type="date"
                      className="w-full md:w-40 p-2 border border-gray-300 rounded text-sm sm:text-base"
                      value={vax.date}
                      onChange={(e) => {
                        const updated = [...vaccinations];
                        updated[index].date = e.target.value;
                        setVaccinations(updated);
                      }}
                      disabled={loading}
                    />
                    {vaccinations.length > 1 && (
                      <button
                        onClick={() => handleDeleteVaccination(index)}
                        className="w-full md:w-auto px-3 py-2 text-xs sm:text-sm bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
                        type="button"
                        disabled={loading}
                      >
                        <Trash className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}

                <div className="flex justify-end mt-3">
                  <button
                    onClick={handleAddVaccination}
                    className="px-3 py-1 text-xs sm:px-4 sm:py-1 sm:text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                    disabled={loading}
                  >
                    + Add Vaccination
                  </button>
                </div>
              </div>

              {/* Deworming Section */}
              <div className="mb-6">
                <label className="block font-semibold text-gray-800 mb-2">
                  Deworming Details
                </label>

                {dewormings.map((entry, index) => (
                  <div
                    key={index}
                    className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4 mb-3 md:mb-2"
                  >
                    <input
                      type="text"
                      className="w-full md:flex-1 p-2 border border-gray-300 rounded text-sm sm:text-base"
                      placeholder="Which deworming?"
                      value={entry.name}
                      onChange={(e) => {
                        const updated = [...dewormings];
                        updated[index].name = e.target.value;
                        setDewormings(updated);
                      }}
                      disabled={loading}
                    />
                    <input
                      type="date"
                      className="w-full md:w-40 p-2 border border-gray-300 rounded text-sm sm:text-base"
                      value={entry.date}
                      onChange={(e) => {
                        const updated = [...dewormings];
                        updated[index].date = e.target.value;
                        setDewormings(updated);
                      }}
                      disabled={loading}
                    />
                    {dewormings.length > 1 && (
                      <button
                        onClick={() => handleDeleteDeworming(index)}
                        className="w-full md:w-auto px-3 py-2 text-xs sm:text-sm bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
                        type="button"
                        disabled={loading}
                      >
                        <Trash className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}

                <div className="flex justify-end mt-3">
                  <button
                    onClick={handleAddDeworming}
                    className="px-3 py-1 text-xs sm:px-4 sm:py-1 sm:text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                    disabled={loading}
                  >
                    + Add Deworming
                  </button>
                </div>
              </div>

              {/* Neutering Section */}
              <div className="mb-6">
                <label className="block font-semibold text-gray-800 mb-2">
                  Spayed / Neutered
                </label>

                {neuteredEntries.map((entry, index) => (
                  <div
                    key={index}
                    className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4 mb-3 md:mb-2"
                  >
                    <div className="w-full md:flex-1 flex items-center gap-4 md:gap-6">
                      <label className="flex items-center gap-1 text-sm sm:text-base">
                        <input
                          type="radio"
                          name={`neutered-${index}`}
                          value="yes"
                          checked={entry.status === "yes"}
                          onChange={() =>
                            handleNeuteredStatusChange(index, "yes")
                          }
                          disabled={loading}
                        />
                        Yes
                      </label>
                      <label className="flex items-center gap-1 text-sm sm:text-base">
                        <input
                          type="radio"
                          name={`neutered-${index}`}
                          value="no"
                          checked={entry.status === "no"}
                          onChange={() =>
                            handleNeuteredStatusChange(index, "no")
                          }
                          disabled={loading}
                        />
                        No
                      </label>
                    </div>
                    <input
                      type="date"
                      className={`w-full md:w-40 p-2 border border-gray-300 rounded text-sm sm:text-base ${
                        entry.status !== "yes"
                          ? "bg-gray-100 text-gray-400"
                          : ""
                      }`}
                      value={entry.date}
                      disabled={entry.status !== "yes" || loading}
                      onChange={(e) => {
                        const updated = [...neuteredEntries];
                        updated[index].date = e.target.value;
                        setNeuteredEntries(updated);
                      }}
                    />
                    {neuteredEntries.length > 1 && (
                      <button
                        onClick={() => handleDeleteNeutered(index)}
                        className="w-full md:w-auto px-3 py-2 text-xs sm:text-sm bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
                        type="button"
                        disabled={loading}
                      >
                        <Trash className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}

                {/* <div className="flex justify-end mt-3">
                  <button
                    onClick={handleAddNeutered}
                    className="px-3 py-1 text-xs sm:px-4 sm:py-1 sm:text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                    disabled={loading}
                  >
                    + Add Spay/Neuter
                  </button>
                </div> */}
              </div>
            </div>

            <div className="flex space-x-4 pt-4">
              <button
                onClick={handleClose}
                disabled={loading}
                className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-700 py-2 px-4 rounded-lg transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={loading || isUploadingImage}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded-lg transition-colors disabled:opacity-50"
              >
                {loading ? "Updating..." : "Update Pet"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditPetModal;
